import { useContext } from 'react';
import { endOfWeek, getYear } from 'date-fns';

import { CalendarView } from '@atom/hooks/useWorkOrdersFilters';
import { Button, Icon, IconButton, Progress } from '@atom/mui';
import {
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  TodayIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

import WorkOrdersContext from '../WorkOrdersContext';

import classes from '../workOrders.module.css';

export interface Props {
  onNavigate: (keyword: string) => void;
  onView: (view: string) => void;
  view: CalendarView;
  label: string;
  loading: boolean;
  date: Date;
}

const styles = {
  button: {
    border: `1px solid ${colors.neutral.ash}`,
    color: colors.neutral.dim,
  },
  toggle: {
    border: 'none',
  },
  selected: {
    background: colors.opacity.black0_1,
  },
};

const WorkOrdersCalendarToolbar = (props: Props) => {
  const { input } = useContext(WorkOrdersContext);
  const { onNavigate, onView, view, loading, label, date } = props;
  const { expanded, workOrderId: previewOpen } = input;

  const squashedCalendar = expanded && previewOpen;

  const handleChange = (option: CalendarView) => {
    onView(option);
  };

  const getToggleStyles = (calendarView: CalendarView) => ({
    ...styles.button,
    ...styles.toggle,
    ...(view === calendarView ? styles.selected : {}),
  });

  const title =
    view === 'week' ? `${label}, ${getYear(endOfWeek(date))}` : label;

  const titleStyle = squashedCalendar ? { fontSize: fonts.sm } : {};
  const titleDate = squashedCalendar ? title.split(',')[0] : title;

  return (
    <div className={classes.calendarToolbarContainer}>
      <div className={classes.calendarToolbarLeftControls}>
        {squashedCalendar ? (
          <IconButton onClick={() => onNavigate('TODAY')}>
            <Icon icon={TodayIcon} />
          </IconButton>
        ) : (
          <Button sx={styles.button} onClick={() => onNavigate('TODAY')}>
            Today
          </Button>
        )}
        <IconButton onClick={() => onNavigate('PREV')}>
          <Icon icon={KeyboardArrowLeftIcon} />
        </IconButton>
        <IconButton onClick={() => onNavigate('NEXT')}>
          <Icon icon={KeyboardArrowRightIcon} />
        </IconButton>
        <div className={classes.dateLabel} style={titleStyle}>
          {titleDate}
        </div>
      </div>
      {!squashedCalendar && (
        <div className={classes.rightContainer}>
          {loading && view === CalendarView.MONTH && (
            <div className={classes.loadingContainer}>
              <Progress size={20} thickness={1.5} />
            </div>
          )}
          <div>
            <Button
              style={getToggleStyles(CalendarView.WEEK)}
              onClick={() => handleChange(CalendarView.WEEK)}
            >
              Week
            </Button>
          </div>
          <Button
            style={getToggleStyles(CalendarView.MONTH)}
            onClick={() => handleChange(CalendarView.MONTH)}
          >
            Month
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkOrdersCalendarToolbar;
